import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseAppService } from '../base-app-service';
import { CONFIG_CONTEXT } from 'src/app/enum/config-context.enum';
import { Configuration } from 'src/app/models/configurators-2d/applications/closet-doors-custom/configuration/configuration';
import { OpeningSystem } from 'src/app/models/configurators-2d/applications/closet-doors-custom/opening-system';
import { SupplierDoorRange } from 'src/app/models/configurators-2d/applications/closet-doors-custom/supplier-door-range';
import { DoorStructure } from 'src/app/models/configurators-2d/applications/closet-doors-custom/door-structure';
import { Sizes } from '../../../models/configurators-2d/applications/closet-doors-custom/sizes';
import { ColorCategory } from 'src/app/models/configurators-2d/applications/closet-doors-custom/color-category';
import { Frame } from 'src/app/models/configurators-2d/applications/closet-doors-custom/frame';
import { IClosetDoorsCustomAdministrationService } from '../administrations/i-closet-doors-custom-administration-service';
import { Supplier } from 'src/app/models/configurators-2d/applications/closet-doors-custom/supplier';
import { rotateImg } from '../../helpers/fence-helper';
import { ConfigurationDoorStructure } from '../../../models/configurators-2d/applications/closet-doors-custom/configuration/configuration-door-structure';
import { ConfigurationDoorSection } from '../../../models/configurators-2d/applications/closet-doors-custom/configuration/configuration-door-section';
import { Resume } from '../../../models/configurators-2d/resume/resume';
import { AccessoryCategory } from 'src/app/models/configurators-2d/applications/closet-doors-custom/accessory-category';
import { ConfigurationAccessory } from '../../../models/configurators-2d/applications/closet-doors-custom/configuration/configuration-accessory';

@Injectable({
  providedIn: 'root',
})
export class ClosetDoorsCustomService
  extends BaseAppService<Configuration>
  implements IClosetDoorsCustomAdministrationService {
  constructor() {
    super(CONFIG_CONTEXT.CLOSET_DOORS_CUSTOM, Configuration);
  }

  protected override simplifyConfiguration(): Configuration {
    let localConfig: Configuration = new Configuration();
    Object.assign(localConfig, this.configuration);

    localConfig.locationId = localConfig.location?.id;
    localConfig.openingSystemId = localConfig.openingSystem?.id;
    localConfig.openingSystemSideId = localConfig.openingSystemSide?.id;
    localConfig.supplierId = localConfig.supplier?.id;
    localConfig.doorRangeId = localConfig.doorRange?.id;
    localConfig.frameId = localConfig.frame?.id;
    localConfig.frameFinishId = localConfig.frameFinish?.id;

    localConfig.location = null;
    localConfig.openingSystem = null;
    localConfig.openingSystemSide = null;
    localConfig.supplier = null;
    localConfig.doorRange = null;
    localConfig.frame = null;
    localConfig.frameFinish = null;

    if (localConfig.configurationDoorStructures?.length > 0) {
      const configurationDoorStructures: ConfigurationDoorStructure[] = [];

      for (const configurationDoorStructure of localConfig.configurationDoorStructures) {
        const { doorStructure, configurationDoorSections, ...localConfigurationDoorStructure } =
          configurationDoorStructure;
        const localConfigurationDoorSections: ConfigurationDoorSection[] = [];

        for (let configurationDoorSection of configurationDoorSections) {
          const { color, doorSection, ...localConfigurationDoorSection } = configurationDoorSection;
          localConfigurationDoorSection.colorId = color.id;
          localConfigurationDoorSection.doorSectionId = doorSection.id;
          localConfigurationDoorSections.push(localConfigurationDoorSection as ConfigurationDoorSection);
        }

        (localConfigurationDoorStructure as ConfigurationDoorStructure).configurationDoorSections =
          localConfigurationDoorSections;
        configurationDoorStructures.push(localConfigurationDoorStructure as ConfigurationDoorStructure);
      }

      localConfig.configurationDoorStructures = configurationDoorStructures;
    }

    if (localConfig.configurationAccessories?.length > 0) {
      const configurationAccessories: ConfigurationAccessory[] = [];

      for (const configurationAccessory of localConfig.configurationAccessories) {
        const { accessory, ...localConfigurationAccessory } = configurationAccessory;
        configurationAccessories.push(localConfigurationAccessory as ConfigurationAccessory);
      }

      localConfig.configurationAccessories = configurationAccessories;
    }

    return localConfig;
  }

  override getResume<R extends Resume>(): Promise<R> {
    return super.getResume<R>(this.simplifyConfiguration());
  }

  /**
   * Fonction de récupération des systèmes d'ouverture et de leur sens d'ouverture
   * @returns
   */
  getOpeningSystems(): Promise<OpeningSystem[]> {
    const params = new HttpParams()
      .set('clientHeight', this.configuration.clientHeight)
      .set('clientWidth', this.configuration.clientWidth);
    return this.get<OpeningSystem[]>(this.apiUrl + 'Configuration/OpeningSystems', params);
  }

  /**
   * Fonction de récupération des tailles min et max
   * @returns
   */
  getHeightLimits(): Promise<Sizes> {
    return this.get<Sizes>(this.apiUrl + 'Configuration/HeightLimits');
  }

  getWidthLimits(): Promise<Sizes> {
    const params = new HttpParams().set('clientHeight', this.configuration.clientHeight);
    return this.get<Sizes>(this.apiUrl + 'Configuration/WidthLimits', params);
  }

  /**
   * Fonction de récupération des fournisseurs.
   * @returns
   */
  getDoorRangesGroupBySupplier(): Promise<SupplierDoorRange[]> {
    const params = new HttpParams()
      .set('clientWidth', this.configuration.clientWidth)
      .set('clientHeight', this.configuration.clientHeight)
      .set('openingSystemId', this.configuration.openingSystem.id)
      .set('doorNumber', this.configuration.nbDoor)
      .set('nbWay', this.configuration.nbWay);
    return this.get<SupplierDoorRange[]>(this.apiUrl + 'Configuration/DoorRanges', params);
  }

  getDoorStructuresAndFinishes(): Promise<DoorStructure[]> {
    const params = new HttpParams()
      .set('doorRangeId', this.configuration.doorRange.id)
      .set('clientWidth', this.configuration.clientWidth)
      .set('clientHeight', this.configuration.clientHeight)
      .set('doorNumber', this.configuration.nbDoor)
      .set('nbWay', this.configuration.nbWay);
    return this.get<DoorStructure[]>(this.apiUrl + 'Configuration/DoorStructures', params);
  }

  getPreview(): Promise<any[]> {
    const params = new HttpParams()
      .set('doorRangeId', this.configuration.doorRange.id)
      .set('frameId', this.configuration.frame.id)
      .set('frameFinishId', this.configuration.frameFinish.id)
      .set('clientHeight', this.configuration.clientHeight)
      .set('clientWidth', this.configuration.clientWidth)
      .set('doorNumber', this.configuration.nbDoor)
      .set('nbWay', this.configuration.nbWay);
    return this.get(this.apiUrl + 'Configuration/Preview', params);
  }

  async getColors(): Promise<ColorCategory[]> {
    try {
      const params = new HttpParams()
        .set('supplierId', this.configuration.supplier.id)
        .set('doorRangeId', this.configuration.doorRange.id)
        .set('clientWidth', this.configuration.clientWidth)
        .set('nbDoor', this.configuration.nbDoor)
        .set('clientHeight', this.configuration.clientHeight)
        .set('nbWay', this.configuration.nbWay);
      const res = await this.get<ColorCategory[]>(this.apiUrl + 'Configuration/Colors', params);

      for (const colorCategory of res) {
        for (const color of colorCategory.colors) {
          // on calcul le base64 des décors en bois
          if (color.isWood) {
            rotateImg(this.apiUrl + color.picture.path).then(function (res) {
              color.rotatePicture = {
                id: color.picture.id,
                name: color.picture.name,
                path: res,
              };
            });
          }
        }
      }

      return res;
    } catch {
      return [];
    }
  }

  getFrames(): Promise<Frame[]> {
    const params = new HttpParams()
      .set('doorRangeId', this.configuration.doorRange.id)
      .set('openingSystemId', this.configuration.openingSystem.id)
      .set('clientHeight', this.configuration.clientHeight)
      .set('clientWidth', this.configuration.clientWidth)
      .set('nbDoor', this.configuration.nbDoor)
      .set('nbWay', this.configuration.nbWay);
    return this.get<Frame[]>(this.apiUrl + 'Configuration/Frames', params);
  }

  getAccessories(): Promise<AccessoryCategory[]> {
    return this.post<AccessoryCategory[]>(`${this.apiUrl}Configuration/Accessories`, this.simplifyConfiguration());
  }

  setAdminAccessoriesPrices(supplierId: number, file: File): Promise<any> {
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);
    const params = new HttpParams().set('supplierId', supplierId);
    return this.post(`${this.apiUrl}Data/UpdateAccessories`, formData, params);
  }

  getSuppliers(): Promise<Supplier[]> {
    return this.get<Supplier[]>(this.apiUrl + 'Data/Suppliers');
  }

  override checkHasDisabledProducts(): void {
    let hasDisabled = this.configuration.configurationAccessories?.some(a => a.accessory.isDisabled);
    this.configuration.hasDisabledProducts = hasDisabled;
  }
}
